/* eslint-disable no-console */
import { Unwanted_Pause_Timeout_Duration_Ms } from '@/globals/constants';
import { VideoElementReadyState } from '@/globals/enums';
import dayjs from 'dayjs';

const LOCAL_STORAGE_LIMIT = 5 * 1024 * 1024; // 5MB limit for localStorage

export const VideoStreamPrint = {
  warnVideoEnded({
    cameraName,
    paused,
    cameraStatus,
    readyState,
    isNoFragmentFound,
    isDiscontinuity,
    timestamp,
    isPlaying,
  }) {
    console.warn(`[${cameraName}]:  
      - Camera is ${cameraStatus}.
      - Video stream is ${
        isPlaying ? 'playing' : 'not playing'
      }(by user or program).
      - Video element is ${paused ? 'paused' : 'not paused'}.
      - The video element ready state with ${VideoElementReadyState[readyState]}
      - ${isNoFragmentFound ? 'No fragment found' : 'Fragment founded'}.
      - ${
        isDiscontinuity
          ? 'Video is in discontinuity mode'
          : 'No discontinuity mode detected'
      }.
      - Timestamp: ${dayjs(timestamp).format('HH:mm:ss')}.`);
  },
  warnDetectUnwantedPause({ cameraName, timestamp, fragTimestamp }) {
    console.warn(
      `[${cameraName}] Unwanted pause detected. Current timestamp is ${dayjs(
        timestamp
      ).format(
        'HH:mm:ss'
      )} but a new fragment with higher timestamp is available ${dayjs(
        fragTimestamp
      ).format('HH:mm:ss')}. Attempting play the video after ${
        Unwanted_Pause_Timeout_Duration_Ms / 1000
      } seconds if the video does not play automatically.`
    );
  },
  warnPlayUnwantedPause({ cameraName, timestamp }) {
    console.warn(
      `[${cameraName}] Unwanted pause detected. Timestamp: ${dayjs(
        timestamp
      ).format('HH:mm:ss')} and it did not play after ${
        Unwanted_Pause_Timeout_Duration_Ms / 1000
      } seconds. Attempting to play the video.`
    );
  },
  warnPlayerLoadLevelExpired({ cameraName, timestamp }) {
    console.warn(
      `[${cameraName}] Streaming session is expired at ${dayjs(
        timestamp
      ).format('HH:mm:ss')}. Attempting to renew the streaming session.`
    );
  },
  warnPlaylistEnding({ cameraName, timestamp, lastFrag }) {
    console.warn(
      `[${cameraName}] Playlist is ended. Last fragment finished by timestamp ${dayjs(
        lastFrag.programDateTime
      ).format('HH:mm:ss')} and duration ${
        lastFrag.duration
      } seconds . Current timestamp is ${dayjs(timestamp).format(
        'HH:mm:ss'
      )}. Attempting to load new playlist.`
    );
  },
  warnReloadLiveOnCheckLive({ cameraName, timestamp }) {
    console.warn(
      `[${cameraName}] Check live triggered as the video stream in not in sync. Attempting to reload the LIVE video stream. Current timestamp is ${dayjs(
        timestamp
      ).format('HH:mm:ss')}.`
    );
  },
  logVideoStreamFragmentChange(thisComponent) {
    console.log(thisComponent.gapTimeList);
    console.log(`[${thisComponent.id}] handleFragmentChanged is called.
      thisComponent.currentFragment: ${thisComponent.currentFragment?.sn}, 
      thisComponent.currentFragment?.programDateTime: ${dayjs(
        thisComponent.currentFragment?.programDateTime
      ).format('HH:mm:ss')}, 
      thisComponent.timestamp: ${dayjs(thisComponent.timestamp).format(
        'HH:mm:ss'
      )}, 
      is video paused: ${thisComponent.remoteViewRef.paused},
      thisComponent.isNextFragmentDiscontinuity: ${
        thisComponent.isNextFragmentDiscontinuity
      }, 
      thisComponent.nextDiscontinuityGap.start: ${dayjs(
        thisComponent.nextDiscontinuityGap?.start
      ).format('HH:mm:ss')}, 
      thisComponent.nextDiscontinuityGap.end: ${dayjs(
        thisComponent.nextDiscontinuityGap?.end
      ).format('HH:mm:ss')}, 
      thisComponent.nextDiscontinuityGap.mediaSequenceNumber: ${
        thisComponent.nextDiscontinuityGap?.mediaSequenceNumber
      }, 
      thisComponent.nextDiscontinuityFrag: ${
        thisComponent.nextDiscontinuityFrag
      }, 
      thisComponent.isTimestampInGap: ${JSON.stringify(
        thisComponent.isTimestampInGap
      )}, 
      thisComponent.isDiscontinuity: ${thisComponent.isDiscontinuity}, 
      thisComponent.isLive: ${thisComponent.isLive}, 
      thisComponent.isNoFragmentFound: ${thisComponent.isNoFragmentFound}, 
      thisComponent.currentTimeIsChanging: ${
        thisComponent.currentTimeIsChanging
      }, 
      thisComponent.waitingTimestampCallerId: ${
        thisComponent.waitingTimestampCallerId
      }, 
      thisComponent.waitingBackwardTimestamp: ${dayjs(
        thisComponent.waitingBackwardTimestamp
      ).format('HH:mm:ss')}, 
      thisComponent.waitingForwardTimestamp: ${dayjs(
        thisComponent.waitingForwardTimestamp
      ).format('HH:mm:ss')}, 
      thisComponent.gapStartTime: ${dayjs(thisComponent.gapStartTime).format(
        'HH:mm:ss'
      )},
      `);
  },
};

export function tracer(functionName, argsObject, state) {
  // only log in local environment
  if (process.env.NODE_ENV === 'development') {
    if (window.location.hostname !== 'localhost') {
      return;
    }
    let timeline = JSON.parse(localStorage.getItem('functionTimeline')) || [];
    const safeStringify = (obj) => {
      const seen = new WeakSet();
      return JSON.stringify(
        obj,
        (key, value) => {
          if (typeof value === 'object' && value !== null) {
            if (seen.has(value)) {
              return '[Circular]';
            }
            seen.add(value);
          }
          if (
            value &&
            value.constructor &&
            value.constructor.name === 'Fragment'
          ) {
            return { ...value, hole: { ...(value.hole ? value.hole : {}) } };
          }
          if (
            value &&
            value.constructor &&
            value.constructor.name === 'XhrLoader'
          ) {
            return '[XhrLoader]';
          }
          return value;
        },
        2
      );
    };
    const now = new Date();
    const timeString = now.toISOString().slice(11, 23); // extracts time part including milliseconds

    const traceEntry = {
      time: timeString,
      functionName: functionName,
      args: argsObject,
      state: state || {},
    };

    timeline.push(traceEntry);

    // keep only the last 300 items
    if (timeline.length > 300) {
      timeline = timeline.slice(-300);
    }

    // check if the size exceeds the local storage limit
    let timelineString = safeStringify(timeline);
    while (timelineString.length > LOCAL_STORAGE_LIMIT) {
      timeline.shift(); // remove the oldest entry
      timelineString = safeStringify(timeline);
    }

    localStorage.setItem('functionTimeline', timelineString);
  }
}

export function clearTracer() {
  localStorage.removeItem('functionTimeline');
}
